import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { WarehouseCareService } from './warehouse-care.service';
import { CareIssue, CareDashboard, CareEvent, CareSettings, CareTask } from './warehouse-care.types';

@Component({selector:'app-warehouse-care',templateUrl:'./warehouse-care.component.html',styleUrls:['./warehouse-care.component.scss']})
export class WarehouseCareComponent implements OnInit, OnDestroy {
  @ViewChild('issueDialog') issueDialog!:TemplateRef<unknown>;
  issue:CareIssue|null=null; problemType='';
  readonly problemTypes:Record<string,string>={NOT_FOUND:'Товар не найден',NO_BARCODE:'Нет штрихкода на упаковке',BARCODE_CONFLICT:'Код другого товара',LOCATION_UNAVAILABLE:'Адрес недоступен',OTHER:'Другая проблема',UNCLASSIFIED:'Требует классификации'};
  readonly issueStatuses:Record<string,string>={OPEN:'Ожидает решения',WAITING:'Ожидает проверки',VERIFYING:'Проверяется',REVIEW:'Расхождение',RESOLVED:'Решено'};
  @ViewChild('taskDialog') taskDialog!:TemplateRef<unknown>;
  private detailDialog?:MatDialogRef<unknown>;
  warehouses:CareSettings[]=[]; users:Array<{id:number;name:string}>=[];
  selected:CareSettings|null=null; dashboard:CareDashboard|null=null; detail:CareTask|null=null; events:CareEvent[]=[];
  canConfigure=false; busy=false; error=''; message=''; reason=''; offset=0;
  pending:{key:string;id:string}|null=null;
  readonly kinds:Record<string,string>={COUNT_PRODUCT:'Пересчитать товар',COUNT_SHELF:'Проверить полку',PLACE:'Назначить адрес',BARCODE:'Добавить штрихкод',CONSOLIDATE:'Объединить товар на полках'};
  readonly statuses:Record<string,string>={SUSPENDED:'Приостановлено',WAITING:'Ожидает',OPEN:'Свободно',IN_PROGRESS:'В работе',REVIEW:'Расхождение',BLOCKED:'Проблема',DONE:'Выполнено',OBSOLETE:'Неактуально',REJECTED:'Отклонено'};
  readonly actions:Record<string,string>={CONSOLIDATED:'Остаток перенесён между полками',MESSAGE:'Сообщение оператора',LEGACY_MESSAGE:'Ранее сообщённая проблема',WAITING:'Ожидание',VERIFIED:'Проверено',RESUMED:'Работа возобновлена',SUSPENDED:'Работа приостановлена',RESOLVED:'Обращение закрыто',MERGED:'Обращения объединены',CLASSIFY:'Причина уточнена',FALSE_REPORT:'Сообщение ошибочно',VERIFY:'Назначена проверка наличия',NOT_REQUIRED:'Работа не требуется',INVENTORY_REMOVED:'Переучёт удалён',CLAIM:'Начато',SAVE:'Черновик сохранён',RELEASE:'Отложено',SUBMIT:'Результат отправлен',RESULT:'Результат',PROBLEM:'Проблема',APPROVE:'Исправление подтверждено',REJECT:'Отклонено',RECOUNT:'Повторная проверка',OBSOLETE:'Неактуально',RECOUNT_SOURCE:'Запрос проверки после отбора'};
  constructor(private readonly api:WarehouseCareService,private readonly dialog:MatDialog,private readonly route:ActivatedRoute) {}
  ngOnDestroy() {this.detailDialog?.close();}
  ngOnInit() {void this.run(async()=>{
    const response=await firstValueFrom(this.api.settings());
    this.warehouses=response.warehouses;this.users=response.users;this.canConfigure=response.canConfigure;
    const warehouseId=Number(this.route.snapshot.queryParamMap.get('warehouseId'));
    this.selected=(Number.isInteger(warehouseId)&&warehouseId>0?this.warehouses.find(w=>w.warehouseId===warehouseId):undefined)
      ||this.warehouses.find(w=>w.canReview)||this.warehouses[0]||null;
    await this.load();
  });}
  private async run(action:()=>Promise<void>) {
    if(this.busy)return;this.busy=true;this.error='';this.message='';
    try{await action();}catch(e:any){const message=typeof e==='string'||Array.isArray(e)?e:e?.error?.message||e?.message;this.error=Array.isArray(message)?message.join(', '):message||'Не удалось выполнить запрос';}
    finally{this.busy=false;}
  }
  private async load() {
    this.detailDialog?.close();
    this.detail=null;this.events=[];
    this.dashboard=this.selected?.canReview?await firstValueFrom(this.api.dashboard(this.selected.warehouseId,this.offset)):null;
  }
  select() {this.offset=0;void this.run(()=>this.load());}
  refresh() {void this.run(()=>this.load());}
  page(delta:number){this.offset=Math.max(0,this.offset+delta);this.refresh();}
  private operation(key:string) {
    if(this.pending?.key!==key)this.pending={key,id:crypto.randomUUID()};
    return this.pending.id;
  }
  save() {void this.run(async()=>{
    const settings=this.selected;if(!settings)return;
    if(Object.values(settings.estimates).some(value=>!Number.isInteger(value)||value<1||value>30))
      throw new Error('Оценка времени каждой задачи должна быть целым числом от 1 до 30 минут');
    await firstValueFrom(this.api.save(settings,this.operation(JSON.stringify(settings))));
    this.pending=null;this.message='Настройки сохранены. Проверки доступны по запросу на ТСД.';
  });}
  open(task:CareTask) {void this.run(async()=>{
    this.detailDialog?.close();
    this.detail=task;this.reason='';this.events=[];
    const ref=this.dialog.open(this.taskDialog,{width:'900px',maxWidth:'96vw',maxHeight:'90vh',
      ariaLabelledBy:'care-task-title',autoFocus:'dialog',restoreFocus:true});
    this.detailDialog=ref;
    ref.afterClosed().subscribe(()=>{
      if(this.detailDialog===ref){this.detailDialog=undefined;this.detail=null;this.events=[];}
    });
    const events=await firstValueFrom(this.api.history(task.id));
    if(this.detailDialog===ref)this.events=events;
  });}
  openIssue(id:number) {void this.run(async()=>{
    this.detailDialog?.close();this.reason='';this.problemType='';this.issue=await firstValueFrom(this.api.issue(id));
    this.detailDialog=this.dialog.open(this.issueDialog,{width:'960px',maxWidth:'96vw',maxHeight:'90vh',autoFocus:'dialog'});
  });}
  resolve(decision:string) {void this.run(async()=>{
    const issue=this.issue;if(!issue)return;
    if(!this.reason.trim())throw new Error('Укажите объяснение решения');
    const key=JSON.stringify({id:issue.id,revision:issue.revision,decision,reason:this.reason,type:this.problemType});
    const ref=this.detailDialog;if(ref)ref.disableClose=true;
    try {await firstValueFrom(this.api.resolve(issue,decision,this.reason,this.operation(key),decision==='CLASSIFY'?this.problemType:undefined));}
    finally {if(ref)ref.disableClose=false;}
    this.pending=null;await this.load();this.message='Решение сохранено. Связанная работа пересмотрена.';
  });}
  isVerified(issue:CareIssue,fact: {productId:number;cellId:number}) {return issue.evidence?.some(e=>e.cellId===fact.cellId&&e.productId===fact.productId);}
  review(decision:string) {void this.run(async()=>{
    const task=this.detail;if(!task)return;
    if(decision!=='APPROVE'&&!this.reason.trim())throw new Error('Укажите причину решения');
    const key=JSON.stringify({id:task.id,revision:task.revision,decision,reason:this.reason});
    const ref=this.detailDialog;
    if(ref)ref.disableClose=true;
    try{await firstValueFrom(this.api.review(task,decision,this.reason,this.operation(key)));}
    finally{if(ref)ref.disableClose=false;}
    this.pending=null;await this.load();this.message=decision==='APPROVE'?'Остатки исправлены, решение записано в журнал.':'Решение сохранено.';
  });}
  product(task:CareTask,id:number){const p=task.products.find(p=>p.id===id);return p?`${p.brand.description} ${p.article}`:`Товар №${id}`;}
  cell(task:CareTask,id:number){return (task.result?.snapshot||task.snapshot)?.rows.find(r=>r.cellId===id)?.code||task.cellCode||`№${id}`;}
  trackTask(_:number,task:CareTask){return task.id;}
}
